import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { IdeaBrief } from "@/components/MoodboardExtras";
import StudioShell from "@/components/StudioShell";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function DesignDirections() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/projects/${id}`).then(r => setProject(r.data)); }, [id]);

  if (!project) return <StudioShell><div className="p-12 text-sm text-stone-500">Loading…</div></StudioShell>;
  const dirs = project.directions || [];

  const refine = async (brief) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${id}/directions/refine`, { brief });
      setProject(p => ({ ...p, directions: r.data.directions, briefs: { ...(p.briefs || {}), directions: brief } }));
      toast.success("Directions re-imagined around your brief");
    } catch (e) { toast.error(apiError(e, "Could not refine directions")); }
    setBusy(false);
  };

  const approve = async (dir) => {
    setBusy(true);
    try {
      await api.post(`/projects/${id}/select-direction`, { direction_id: dir.id, brief: project.briefs?.directions || "" });
      toast.success("Direction approved");
      navigate(`/project/${id}/dna`);
    } catch { toast.error("Failed to approve direction"); }
    setBusy(false);
  };

  return (
    <StudioShell projectContext={project}>
      <div className="px-6 sm:px-12 py-12 max-w-[1440px] mx-auto">
        <div className="eyebrow mb-3">Design Direction Studio</div>
        <h1 className="editorial-heading text-4xl sm:text-5xl mb-2">Three curated directions</h1>
        <p className="text-stone-500 mb-12 max-w-2xl">Each direction is a distinct spatial hypothesis derived from the client's questionnaire and uploaded references. Choose one to develop into a full mood board and presentation.</p>

        <IdeaBrief index="directions" value={project.briefs?.directions} busy={busy} onRegenerate={refine}
          label="Your ideas before we go further" cta="Re-imagine the 3 directions"
          placeholder="e.g. The client loves Japandi calm but wants one bold jewel colour; avoid grey; the house has strong west light…" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {dirs.map(d => (
            <div key={d.id} className={`studio-card overflow-hidden flex flex-col ${selected===d.id ? "border-black" : ""}`} data-testid={`direction-card-${d.id}`}>
              <div className="p-8 flex-1 flex flex-col">
                <div className="eyebrow mb-3">{d.id.replace("dir_","Direction 0")}</div>
                <h3 className="font-serif-editorial text-2xl mb-2">{d.name.replace(/^Direction [ABC]: /,"")}</h3>
                <div className="text-xs text-stone-500 mb-5">{d.subtitle}</div>
                <div className="grid grid-cols-5 gap-1 mb-6">
                  {(d.palette||[]).slice(0,5).map((p)=>(<div key={`${p.hex}-${p.name}`} className="h-16" style={{ background: p.hex }} title={p.name} />))}
                </div>
                <p className="text-sm text-stone-600 leading-relaxed mb-5">{d.description}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {(d.keywords||[]).slice(0,5).map(k => <span key={k} className="text-[11px] font-mono-lab uppercase tracking-wider text-stone-500 border border-stone-200 px-2 py-1">{k}</span>)}
                </div>
                <div className="mb-5">
                  <div className="eyebrow mb-2">Materials</div>
                  <div className="text-sm text-stone-700">{(d.materials||[]).join(" · ")}</div>
                </div>
                <div className="mt-auto pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="font-serif-editorial italic text-sm text-stone-700 mb-5">"{d.mood_quote}"</div>
                  <button onClick={()=>approve(d)} disabled={busy} data-testid={`select-direction-${d.id}`} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-black text-white text-sm disabled:opacity-50">
                    Use This Direction <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </StudioShell>
  );
}
